import {
  IconArrowRight,
  IconBrandLinkedin,
  IconBrandX,
  IconBrandYoutube,
} from "@tabler/icons-react";
import { Link } from "react-router";
import { Logo } from "./logo";

const FOOTER_LINKS = {
  product: [
    { label: "Pricing", href: "/#pricing" },
    { label: "Services", href: "/#services" },
    { label: "How it works", href: "/#how-it-works" },
  ],
  account: [
    { label: "Get started", href: "https://app.titanskip.com" },
    { label: "Sign in", href: "https://app.titanskip.com" },
    { label: "API docs", href: "https://app.titanskip.com/api-docs" },
    { label: "Support", href: "https://app.titanskip.com/support" },
  ],
  legal: [
    { label: "Privacy Policy", href: "/legal/privacy" },
    { label: "Terms of Service", href: "/legal/terms" },
  ],
};

const SOCIALS = [
  { label: "X", icon: IconBrandX, href: "#" },
  { label: "LinkedIn", icon: IconBrandLinkedin, href: "#" },
  { label: "YouTube", icon: IconBrandYoutube, href: "#" },
];

function FooterColumn({ title, links }) {
  return (
    <div>
      <h4 className="mb-4 text-[10px] font-semibold uppercase tracking-[0.22em] text-text-muted">
        {title}
      </h4>
      <ul className="space-y-3">
        {links.map((link) => (
          <li key={link.label}>
            <a
              href={link.href}
              className="text-sm text-text-soft transition hover:text-main-300"
            >
              {link.label}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SocialLinks() {
  return (
    <div className="flex flex-wrap gap-2">
      {SOCIALS.map((s) => (
        <a
          key={s.label}
          href={s.href}
          aria-label={s.label}
          className="grid h-10 w-10 place-items-center rounded-full text-text-muted shadow-[inset_0_0_0_1px_rgba(255,255,255,0.1)] transition hover:text-main-300 hover:shadow-[inset_0_0_0_1px_rgba(63,96,214,0.4)]"
        >
          <s.icon size={16} stroke={1.8} />
        </a>
      ))}
    </div>
  );
}

export function Footer() {
  return (
    <footer className="relative overflow-hidden bg-bg-elevated px-6 pt-24 pb-12 sm:px-8">
      <div className="relative mx-auto max-w-400">
        <div className="grid gap-4 lg:grid-cols-12">

          {/* ── CTA card ── col-span-12 (top) */}
          <div className="col-span-12 overflow-hidden rounded-3xl bg-main-500 p-7 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.18)]">
            <div className="grid gap-6 lg:grid-cols-[1fr_auto] lg:items-center">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-white/60">
                  Ready to trace?
                </p>
                <h3 className="mt-3 text-3xl font-bold tracking-[-0.04em] text-white sm:text-4xl">
                  Start with 1,000 credits and scale from there.
                </h3>
              </div>

              <a
                href="https://app.titanskip.com"
                className="group inline-flex h-12 shrink-0 items-center gap-2 self-start rounded-full bg-main-900/40 pl-6 pr-4 text-sm font-semibold text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.18)] transition hover:bg-main-900/60 lg:self-auto"
              >
                Buy credits
                <span
                  aria-hidden
                  className="grid h-7 w-7 place-items-center rounded-full bg-white/15 transition group-hover:translate-x-0.5 group-hover:bg-white/25"
                >
                  <IconArrowRight size={14} stroke={2.6} />
                </span>
              </a>
            </div>
          </div>

          {/* ── Brand card ── col-span-5 */}
          <div className="col-span-12 rounded-3xl bg-card-2 p-7 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.07),inset_0_1px_0_0_rgba(255,255,255,0.06)] lg:col-span-5">
            <div className="flex items-center gap-3">
              <Logo />
              <p className="text-sm text-text-muted">
                Property data, ready to dial.
              </p>
            </div>

            <h2 className="mt-10 max-w-md text-3xl font-bold tracking-[-0.04em] text-text sm:text-4xl">
              Clean contacts without the manual work.
            </h2>
            <p className="mt-5 max-w-md text-base leading-7 text-text-soft">
              Bulk skip tracing, LLC owner resolution, DNC scrubbing, and
              CRM-ready exports — all in one platform.
            </p>
          </div>

          {/* ── Links card ── col-span-7 */}
          <div className="col-span-12 rounded-3xl bg-bg p-7 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.06)] lg:col-span-7">
            <div className="grid gap-8 sm:grid-cols-3">
              <FooterColumn title="Product" links={FOOTER_LINKS.product} />
              <FooterColumn title="Account" links={FOOTER_LINKS.account} />
              <FooterColumn title="Legal" links={FOOTER_LINKS.legal} />
            </div>

            <div className="mt-10 flex flex-col justify-between gap-5 border-t border-white/8 pt-6 md:flex-row md:items-center">
              <a
                href="mailto:hello@titanskip.com"
                className="text-sm text-text-muted transition hover:text-text"
              >
                hello@titanskip.com
              </a>
              <SocialLinks />
            </div>
          </div>

        </div>

        {/* Legal row */}
        <div className="mt-8 flex flex-col justify-between gap-5 text-sm text-text-muted md:flex-row md:items-center">
          <p>© {new Date().getFullYear()} TitanSkip. All rights reserved.</p>
          <div className="flex flex-wrap gap-5">
            <Link to="/legal/privacy" className="transition hover:text-text">
              Privacy Policy
            </Link>
            <Link to="/legal/terms" className="transition hover:text-text">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
